/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, integer, boolean, timestamp, json } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Topic categories
export const TOPIC_CATEGORIES = {
  CONCEPT: 'concept',
  PERSON: 'person',
  EVENT: 'event',
  ACADEMIC_DOMAIN: 'academic_domain',
  SUBDOMAIN: 'subdomain',
} as const;

export type TopicCategory = typeof TOPIC_CATEGORIES[keyof typeof TOPIC_CATEGORIES];

// Topic replationship labels
export const TOPIC_RELATIONS = {
  CONTAINS: 'contains',
  IS_A: 'is_a',         // topic A belongs to category represented by topic B
  RELATED: 'related',
  PREREQUISITE: 'prerequisite'
} as const;

export type TopicRelations = typeof TOPIC_RELATIONS[keyof typeof TOPIC_RELATIONS];

// Skill levels (Bloom's taxonomy)
export const SKILL_LEVEL = {
  REMEMBER: 1,
  UNDERSTAND: 2,
  APPLY: 3,
  ANALYZE: 4,
  EVALUATE: 5,
  CREATE: 6,
} as const;

export type SkillLevel = typeof SKILL_LEVEL[keyof typeof SKILL_LEVEL];

// Skill replationship labels
export const SKILL_RELATIONS = {
  RELATED: 'related',
  PREREQUISITE: 'prerequisite',
  BUILDS_ON: 'builds_on'
} as const;

export type SkillRelations = typeof SKILL_RELATIONS[keyof typeof SKILL_RELATIONS];

export const topics = pgTable("topics", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  description: text("description"),
  category: text("category").notNull(),
  parentId: integer("parent_id"),
  tags: text("tags").array(),
});

export const skills = pgTable("skills", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  description: text("description"),
  level: integer("level").notNull(), // integer SKILL_LEVEL corresponding to level in Bloom's taxonomy
  parentId: integer("parent_id"),
  relatedTopicIds: integer("related_topic_ids").array(),
  associatedContentCount: integer("associated_content_count").default(0),
  tags: text("tags").array(),
  skillData: json("skill_data"), // JSON field to store metadata like extended description
});

// Skill Libraries - collections of related skills
export const skillLibraries = pgTable("skill_libraries", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  thumbnailUrl: text("thumbnail_url"),
  skillIds: integer("skill_ids").array(), // Array of skill IDs in this library
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Topic Libraries - collections of related topics  
export const topicLibraries = pgTable("topic_libraries", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  thumbnailUrl: text("thumbnail_url"),
  topicIds: integer("topic_ids").array(), // Array of topic IDs in this library
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Skill Graphs - collections of related skills
export const skillGraphs = pgTable("skill_graphs", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  thumbnailUrl: text("thumbnail_url"),
  nodeIds: integer("node_ids").array(), // Array of skill IDs in this graph
  relationIds: integer("relation_ids").array(), // Array of relation IDs (edges) in this graph
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdById: integer("created_by_id"),
  isPublic: boolean("is_public").default(false),
});

// Topic Graphs - collections of related topics
export const topicGraphs = pgTable("topic_graphs", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description"),
  thumbnailUrl: text("thumbnail_url"),
  nodeIds: integer("node_ids").array(), // Array of topic IDs in this graph
  relationIds: integer("relation_ids").array(), // Array of relation IDs (edges) in this graph
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdById: integer("created_by_id"),
  isPublic: boolean("is_public").default(false),
});

// Skill Relations - edges between skills in a skill graph
export const skillRelations = pgTable("skill_relations", {
  id: serial("id").primaryKey(),
  graphId: integer("graph_id").notNull(), // Reference to the skill graph
  sourceSkillId: integer("source_skill_id").notNull(), // Source skill ID
  targetSkillId: integer("target_skill_id").notNull(), // Target skill ID
  label: text("label"), // Optional relationship label (e.g., "prerequisite", "builds upon")
  strength: integer("strength"), // Optional field to indicate relationship strength (1-10)
  createdAt: timestamp("created_at").defaultNow(),
});

// Topic Relations - edges between topics in a topic graph
export const topicRelations = pgTable("topic_relations", {
  id: serial("id").primaryKey(),
  graphId: integer("graph_id").notNull(), // Reference to the topic graph
  sourceTopicId: integer("source_topic_id").notNull(), // Source topic ID
  targetTopicId: integer("target_topic_id").notNull(), // Target topic ID
  label: text("label"), // Optional relationship label (e.g., "includes", "part of")
  strength: integer("strength"), // Optional field to indicate relationship strength (1-10)
  createdAt: timestamp("created_at").defaultNow(),
});

// Insert schemas
export const insertTopicSchema = createInsertSchema(topics).omit({ id: true });
export const insertSkillSchema = createInsertSchema(skills).omit({ id: true });
export const insertSkillLibrarySchema = createInsertSchema(skillLibraries).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTopicLibrarySchema = createInsertSchema(topicLibraries).omit({ id: true, createdAt: true, updatedAt: true });
export const insertSkillGraphSchema = createInsertSchema(skillGraphs).omit({ id: true });
export const insertTopicGraphSchema = createInsertSchema(topicGraphs).omit({ id: true });
export const insertSkillRelationSchema = createInsertSchema(skillRelations).omit({ id: true });
export const insertTopicRelationSchema = createInsertSchema(topicRelations).omit({ id: true });

// Types
export type Topic = typeof topics.$inferSelect;
export type InsertTopic = z.infer<typeof insertTopicSchema>;
export type Skill = typeof skills.$inferSelect;
export type InsertSkill = z.infer<typeof insertSkillSchema>;
export type SkillLibrary = typeof skillLibraries.$inferSelect;
export type InsertSkillLibrary = z.infer<typeof insertSkillLibrarySchema>;
export type TopicLibrary = typeof topicLibraries.$inferSelect;
export type InsertTopicLibrary = z.infer<typeof insertTopicLibrarySchema>;
export type SkillGraph = typeof skillGraphs.$inferSelect;
export type InsertSkillGraph = z.infer<typeof insertSkillGraphSchema>;
export type TopicGraph = typeof topicGraphs.$inferSelect;
export type InsertTopicGraph = z.infer<typeof insertTopicGraphSchema>;
export type SkillRelation = typeof skillRelations.$inferSelect;
export type InsertSkillRelation = z.infer<typeof insertSkillRelationSchema>;
export type TopicRelation = typeof topicRelations.$inferSelect;
export type InsertTopicRelation = z.infer<typeof insertTopicRelationSchema>; 